import type { FC, ReactNode } from "react";

import {
  Badge,
  Box,
  Column,
  Popover,
  PopoverBody,
  PopoverContent,
  Portal,
  Row,
  Text,
  TextWithTooltip,
} from "@hightouch/htui";

import { RouterLink } from "src/router";

import { PopoverTriggerButton } from "./popover-trigger-button";

export type PopoverNavMenuItem = {
  id: string;
  to: string;
  label: string;
  /** Optional leading visual (avatar, icon, etc.). */
  leading?: ReactNode;
};

export type PopoverNavMenuProps = {
  trigger: ReactNode;
  triggerAriaLabel: string;
  title: string;
  items: PopoverNavMenuItem[];
};

export const PopoverNavLinkRow: FC<
  Pick<PopoverNavMenuItem, "to" | "label" | "leading">
> = ({ to, label, leading }) => (
  <Row
    as={RouterLink}
    to={to}
    align="center"
    gap={3}
    px={2}
    py={1.5}
    borderRadius="xl"
    cursor="pointer"
    outlineColor="transparent"
    _hover={{
      bg: "primary.background",
      color: "primary.base",
      outline: "1px solid",
      outlineColor: "primary.border",
      outlineWidth: "0.5px",
    }}
    minW="198px"
  >
    {leading}
    <Box minW={0}>
      <TextWithTooltip fontWeight="medium">{label}</TextWithTooltip>
    </Box>
  </Row>
);

/**
 * Hover popover with a titled header and styled router links. Intended for
 * compact table cells where a trigger reveals a short nav menu (e.g. domain or
 * source stacks).
 */
export const PopoverNavMenu: FC<PopoverNavMenuProps> = ({
  trigger,
  triggerAriaLabel,
  title,
  items,
}) => (
  <Popover isLazy trigger="hover" placement="bottom-start">
    <PopoverTriggerButton
      aria-label={triggerAriaLabel}
      display="inline-flex"
      borderRadius="full"
    >
      {trigger}
    </PopoverTriggerButton>
    <Portal>
      <PopoverContent w="fit-content">
        <PopoverBody p={2}>
          <Column gap={1}>
            <Box px={2} gap={2} display="flex" alignItems="center">
              <Text color="text.secondary" size="sm" fontWeight="medium">
                {title}
              </Text>
              <Badge variant="subtle" size="sm">
                {items.length}
              </Badge>
            </Box>
            {items.map(({ id, to, label, leading }) => (
              <PopoverNavLinkRow
                key={id}
                to={to}
                label={label}
                leading={leading}
              />
            ))}
          </Column>
        </PopoverBody>
      </PopoverContent>
    </Portal>
  </Popover>
);
